import {Component, OnDestroy, OnInit} from '@angular/core';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { environment } from 'src/environments/environment';
import { GlobalMessageService } from './auth/services/global-message.service';
import { filter, map, pairwise } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { Router, RoutesRecognized } from '@angular/router';
import { LocalStorageService } from 'ngx-webstorage';
import { StorageInformation } from './shared/constants/storage-information';
import { AppInsightsService } from './app-insights.service';
import { LoadingService } from './shared/services/loading.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  subscription: Subscription;

  backend = environment.api_url;

  menuMode = 'static';

  layout = 'blue';

  theme = 'blue';

  ripple: boolean;

  colorScheme = 'light';

  appName = 'Servicio de Protección Especializada a la Niñez y Adolescencia';

  currentDate = new Date();
  year = this.currentDate.getFullYear();

  loading$: Observable<boolean>;

  constructor(private primengConfig: PrimeNGConfig,
    private readonly messageService: MessageService,
    private router: Router,
    private readonly storage: LocalStorageService,
    private readonly globalMessageService: GlobalMessageService,
    private readonly appInsightsService: AppInsightsService,
    private readonly loadingService: LoadingService) {

    this.loading$ = this.loadingService.loading$;

    this.subscription = router.events.pipe(filter((e: any) => e instanceof RoutesRecognized),
      pairwise()).subscribe((e: any) => {
      this.storage.store(StorageInformation.lastPage, e[0].urlAfterRedirects);
    });
  }

  ngOnInit() {
    this.primengConfig.ripple = true;
    this.ripple = true;
    // Configuración de traducciones para PrimeNG (español)
    this.primengConfig.setTranslation({
      startsWith: 'Empieza con',
      contains: 'Contiene',
      notContains: 'No contiene',
      endsWith: 'Termina con',
      equals: 'Igual a',
      notEquals: 'Diferente de',
      noFilter: 'Sin filtro',
      lt: 'Menor que',
      lte: 'Menor o igual que',
      gt: 'Mayor que',
      gte: 'Mayor o igual que',
      dateIs: 'Fecha es',
      dateIsNot: 'Fecha no es',
      dateBefore: 'Fecha anterior',
      dateAfter: 'Fecha posterior',
      clear: 'Limpiar',
      apply: 'Aplicar',
      matchAll: 'Coincidencia total',
      matchAny: 'Coincidencia parcial',
      addRule: 'Agregar regla',
      removeRule: 'Eliminar regla',
      accept: 'Aceptar',
      reject: 'Rechazar',
      choose: 'Seleccionar',
      upload: 'Subir',
      cancel: 'Cancelar',
      dayNames: ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'],
      dayNamesShort: ['dom','lun','mar','mié','jue','vie','sáb'],
      dayNamesMin: ['D','L','M','X','J','V','S'],
      monthNames: ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'],
      monthNamesShort: ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'],
      today: 'Hoy',
      weekHeader: 'Sm'
    });
    this.globalMessageService.itemsHandler.subscribe(_payload => {
      this.messageService.add({severity: _payload.type, summary: _payload.title, detail: _payload.body});
    });

    this.appInsightsService.logPageView('HomePage');

    // Patch runtime labels/tooltips coming from Form.io that use English defaults
    try {
      const globalAny: any = window as any;
      const Formio = globalAny.Formio || (globalAny as any).formiojs;
      if (Formio && Formio.Components) {
        const components = Formio.Components;
        // Iterate components and patch editForm display labels/tooltips
        Object.keys(components).forEach(key => {
          try {
            const comp = components[key];
            if (comp && comp.prototype && comp.prototype.editForm) {
              const editForm = comp.prototype.editForm();
              // Traducir componentes recursivamente
              this.translateFormioEditForm(editForm);
            }
          } catch (err) {}
        });
      }
    } catch (err) {
      console.warn('[AppComponent] no fue posible parchear Formio en runtime', err);
    }
  }
  
  private translateFormioEditForm(editForm: any) {
    if (!editForm) return;
    
    // Si tiene componentes directos
    if (Array.isArray(editForm.components)) {
      editForm.components.forEach((cfg: any) => {
        this.translateFormioComponent(cfg);
      });
    }
    
    // Si el editForm en sí es un array (algunos componentes lo retornan así)
    if (Array.isArray(editForm)) {
      editForm.forEach((cfg: any) => {
        this.translateFormioComponent(cfg);
      });
    }
  }
  
  private translateFormioComponent(cfg: any) {
    if (!cfg) return;
    
    // Traducir Display Mask
    if (cfg.key === 'displayMask') {
      if (cfg.label === 'Display Mask') {
        cfg.label = 'Máscara de visualización';
      }
      if (cfg.tooltip) {
        // Primero reemplazar textos largos, luego los específicos
        cfg.tooltip = cfg.tooltip
          .replace("A display mask helps to display the input in a readable way, this won't affect the value which will be saved (to affect both view and saved value, delete Display Mask and use Input Mask).", 
            'Una máscara de visualización ayuda a mostrar la entrada de forma legible; esto no afectará el valor que se guardará (para afectar tanto la vista como el valor guardado, elimine la máscara de visualización y use la máscara de entrada).')
          .replace('Display Mask', 'Máscara de visualización')
          .replace('9: numeric', '9: numérico')
          .replace('a: alphabetical', 'a: alfabético')
          .replace('*: alphanumeric', '*: alfanumérico')
          .replace('Example telephone mask:', 'Ejemplo de máscara de teléfono:')
          .replace('See the', 'Ver la')
          .replace('jquery.inputmask documentation', 'documentación de jquery.inputmask')
          .replace('for more information.', 'para más información.');
      }
    }
    
    // Traducir Apply Mask On
    if (cfg.key === 'applyMaskOn') {
      if (cfg.label === 'Apply Mask On') {
        cfg.label = 'Aplicar máscara en';
      }
      if (cfg.tooltip === 'Select the type of applying mask.') {
        cfg.tooltip = 'Seleccione el tipo de aplicación de máscara.';
      }
    }
    
    // Traducir Default Value tooltip
    if (cfg.key === 'defaultValue') {
      if (cfg.tooltip && cfg.tooltip.includes('The Default Value will be the value for this field')) {
        cfg.tooltip = 'El valor por defecto será el valor para este campo, antes de la interacción del usuario. Tener un valor por defecto anulará el texto del marcador de posición.';
      }
    }
    
    // Traducir Validate When Hidden
    if (cfg.key === 'validateWhenHidden') {
      if (cfg.label === 'Validate When Hidden') {
        cfg.label = 'Validar cuando está oculto';
      }
      if (cfg.tooltip) {
        // Reemplazar el texto exacto (nota el error tipográfico "Vaildation" en el original)
        if (cfg.tooltip.includes('Validates the component when it is hidden/conditionally hidden')) {
          cfg.tooltip = 'Valida el componente cuando está oculto/condicionalmente oculto. Los errores de validación se muestran en la alerta de error en el envío del formulario.';
        }
      }
    }
    
    // Traducir Truncate Multiple Spaces
    if (cfg.key === 'truncateMultipleSpaces') {
      if (cfg.label === 'Truncate Multiple Spaces') {
        cfg.label = 'Truncar múltiples espacios';
      }
    }
    
    // Traducir tooltips del componente Survey
    if (cfg.tooltip) {
      // Traducir tooltip de Options/Values en Survey
      if (cfg.tooltip.includes('The values that can be selected per question')) {
        cfg.tooltip = 'Los valores que se pueden seleccionar por pregunta. Ejemplo: \'Satisfecho\', \'Muy satisfecho\', etc.';
      }
    }
    
    // Traducir Custom Errors
    if (cfg.label === 'Custom Errors') {
      cfg.label = 'Errores personalizados';
    }
    
    // Recorrer sub-componentes si existen (componentes anidados)
    if (cfg.components && Array.isArray(cfg.components)) {
      cfg.components.forEach((subCfg: any) => {
        this.translateFormioComponent(subCfg);
      });
    }
    
    // Recorrer columns (para layouts tipo columnas)
    if (cfg.columns && Array.isArray(cfg.columns)) {
      cfg.columns.forEach((col: any) => {
        if (col.components && Array.isArray(col.components)) {
          col.components.forEach((subCfg: any) => {
            this.translateFormioComponent(subCfg);
          });
        }
      });
    }
    
    // Recorrer rows (para layouts tipo filas)
    if (cfg.rows && Array.isArray(cfg.rows)) {
      cfg.rows.forEach((row: any) => {
        if (Array.isArray(row)) {
          row.forEach((cell: any) => {
            if (cell.components && Array.isArray(cell.components)) {
              cell.components.forEach((subCfg: any) => {
                this.translateFormioComponent(subCfg);
              });
            }
          });
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.subscription) { this.subscription.unsubscribe() };
  }

}
